/**
 * CardDetailModal Component - View/Edit Card with Markdown Document
 * Opens card details and loads the associated markdown document for viewing/editing
 *
 * Features:
 * - Tiptap collaborative editor with real-time sync via Yjs
 * - Markdown preview with proper rendering
 * - Review comment capture for approval workflows
 * - AI assistance integration
 *
 * Reference: .equilateral-standards/real-time-systems/websocket_postgres_pattern.md
 */

import React, { useEffect, useState, useCallback } from 'react';
import { FiX, FiEdit2, FiEye, FiSave, FiExternalLink, FiZap, FiCheckCircle, FiTarget, FiCpu, FiClipboard, FiMessageSquare, FiSend, FiUsers } from 'react-icons/fi';
import { CardEditor } from './CardEditor';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface Card {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  state: string;
  priority: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  tags?: {
    s3_url?: string;
    source?: string;
    file_size?: number;
    word_count?: number;
  };
}

interface CardDetailModalProps {
  card: Card;
  onClose: () => void;
  clientId: string;
}

interface ReviewComment {
  id: string;
  user_email: string;
  user_name: string;
  comment: string;
  created_at: string;
  comment_type: 'general' | 'suggestion' | 'approval' | 'rejection';
}

const CardDetailModal: React.FC<CardDetailModalProps> = ({ card, onClose, clientId }) => {
  const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'anonymous';
  const userName = localStorage.getItem('User_Display_Name') || userEmail;
  const [isEditing, setIsEditing] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiAssisting, setAiAssisting] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [useCollaborativeEditor, setUseCollaborativeEditor] = useState(true); // Enabled - WebSocket API Gateway deployed

  // Review comment state
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [reviewComments, setReviewComments] = useState<ReviewComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState<ReviewComment['comment_type']>('general');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Load markdown content
  useEffect(() => {
    loadMarkdown();
  }, [card.card_id]);

  const loadMarkdown = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Always call API - backend handles both S3 docs and fallback content
      console.log('[CardDetailModal] Loading document for card:', card.card_id, 's3_url:', card.tags?.s3_url);

      const response = await Make_Authorized_API_Call<{ content: string; source: string }>(
        API_ENDPOINTS.EA_AGILE.CARD_DOCUMENT_GET,
        'GET',
        undefined,
        { params: { card_id: card.card_id } }
      );

      console.log('[CardDetailModal] Document response:', response);

      if (isSuccessResponse(response)) {
        const record = response.data?.Records?.[0];
        const content = record?.content || '';
        console.log('[CardDetailModal] Loaded content:', {
          source: record?.source,
          contentLength: content.length,
          preview: content.substring(0, 100)
        });
        setMarkdown(content);
      } else {
        throw new Error(response.message || 'Failed to load document');
      }
    } catch (err) {
      console.error('[CardDetailModal] Error loading markdown:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document');
      // No fallback - fail loud, show error state
      setMarkdown('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.EA_AGILE.CARD_DOCUMENT_PUT,
        'PUT',
        { card_id: card.card_id, markdown },
        { params: { card_id: card.card_id } }
      );

      if (isSuccessResponse(response)) {
        console.log('Document saved:', response.data);
        setIsEditing(false);
      } else {
        throw new Error(response.message || 'Failed to save document');
      }
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err instanceof Error ? err.message : 'Failed to save document');
    } finally {
      setIsLoading(false);
    }
  };

  const openInNewTab = () => {
    if (card.tags?.s3_url?.startsWith('file://')) {
      const filePath = card.tags.s3_url.replace('file://', '');
      alert(`File path: ${filePath}\n\nNote: Cannot open local files directly from browser. Open in your file system.`);
    }
  };

  // Load review comments for the card
  const loadReviewComments = useCallback(async () => {
    try {
      const response = await Make_Authorized_API_Call<ReviewComment>(
        API_ENDPOINTS.EA_AGILE.CARD_COMMENTS_GET,
        'GET',
        undefined,
        { params: { card_id: card.card_id, include_comments: true } }
      );

      if (isSuccessResponse(response)) {
        // Tim-Combo format: response.data.Records is the array
        const records = response.data?.Records as ReviewComment[] | undefined;
        setReviewComments(Array.isArray(records) ? records : []);
      }
    } catch (err) {
      console.error('Error loading review comments:', err);
      // Don't show error for comments - they're optional
    }
  }, [card.card_id]);

  // Submit a new review comment
  const handleSubmitComment = useCallback(async () => {
    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.EA_AGILE.CARD_COMMENTS_POST,
        'POST',
        {
          card_id: card.card_id,
          comment: newComment.trim(),
          comment_type: commentType,
          user_email: userEmail,
          user_name: userName
        }
      );

      if (isSuccessResponse(response)) {
        // Add the new comment to the list
        const newReviewComment: ReviewComment = {
          id: response.data?.Records?.[0]?.id || `temp-${Date.now()}`,
          user_email: userEmail,
          user_name: userName,
          comment: newComment.trim(),
          created_at: new Date().toISOString(),
          comment_type: commentType
        };
        setReviewComments(prev => [...prev, newReviewComment]);
        setNewComment('');
        setCommentType('general');
      } else {
        throw new Error(response.message || 'Failed to submit comment');
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit comment');
    } finally {
      setIsSubmittingComment(false);
    }
  }, [card.card_id, newComment, commentType, userEmail, userName]);

  // Load comments when panel is opened
  useEffect(() => {
    if (showReviewPanel) {
      loadReviewComments();
    }
  }, [showReviewPanel, loadReviewComments]);

  // Format comment type for display
  const getCommentTypeStyle = (type: ReviewComment['comment_type']) => {
    switch (type) {
      case 'approval':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejection':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'suggestion':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Simple markdown to HTML converter for preview
  const renderMarkdown = (text: string): string => {
    return text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/gim, '<pre class="bg-gray-100 p-3 rounded-lg overflow-x-auto my-3 font-mono text-sm"><code>$1</code></pre>')
      .replace(/`(.*?)`/gim, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      // Lists
      .replace(/^\s*[-*]\s+(.*$)/gim, '<li class="ml-4">$1</li>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
      // Line breaks
      .replace(/\n\n/gim, '</p><p class="my-3">')
      .replace(/\n/gim, '<br/>')
      // Horizontal rules
      .replace(/^---$/gim, '<hr class="my-6 border-gray-300"/>')
      // Wrap in paragraph
      .replace(/^(.+)$/gim, '<p class="my-3">$1</p>');
  };

  const handleAiAssist = async (assistanceType: string, framework?: string) => {
    setAiAssisting(true);
    setError('');
    setShowAiMenu(false);

    try {
      const requestBody: { card_id: string; assistance_type: string; framework?: string } = {
        card_id: card.card_id,
        assistance_type: assistanceType,
      };

      if (framework) {
        requestBody.framework = framework;
      }

      const response = await Make_Authorized_API_Call<{ ai_response: string }>(
        API_ENDPOINTS.EA_AGILE.CARD_DOCUMENT_ASSIST,
        'POST',
        requestBody
      );

      if (isSuccessResponse(response)) {
        const aiResponse = response.data?.Records?.[0]?.ai_response;

        // Append AI suggestions to markdown
        setMarkdown((prev) => {
          const separator = '\n\n---\n\n';
          const aiSection = `## AI Assistance: ${assistanceType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}${framework ? ` (${framework})` : ''}\n\n${aiResponse}`;
          return prev + separator + aiSection;
        });

        // Automatically switch to editing mode to allow user to integrate suggestions
        setIsEditing(true);
      } else {
        throw new Error('AI assistance request failed');
      }
    } catch (err) {
      console.error('AI assist error:', err);
      setError(err instanceof Error ? err.message : 'AI assistance failed');
    } finally {
      setAiAssisting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                {card.card_type}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                card.priority === 'high' ? 'bg-red-100 text-red-800' :
                card.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {card.priority}
              </span>
              <span className="text-xs text-gray-500">
                {card.tags?.word_count} words • {Math.round((card.tags?.file_size || 0) / 1024)}KB
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{card.title}</h2>
            {card.description && (
              <p className="text-sm text-gray-600 mt-2">{card.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isEditing
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {isEditing ? <FiEye className="w-4 h-4" /> : <FiEdit2 className="w-4 h-4" />}
              {isEditing ? 'Preview' : 'Edit'}
            </button>
            {isEditing && (
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FiSave className="w-4 h-4" />
                Save
              </button>
            )}

            {/* Review Comments Toggle */}
            <button
              onClick={() => setShowReviewPanel(!showReviewPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showReviewPanel
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <FiMessageSquare className="w-4 h-4" />
              Comments
              {reviewComments.length > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  showReviewPanel ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-800'
                }`}>
                  {reviewComments.length}
                </span>
              )}
            </button>

            {/* Collaborative Editor Toggle */}
            <button
              onClick={() => setUseCollaborativeEditor(!useCollaborativeEditor)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                useCollaborativeEditor
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
              title={useCollaborativeEditor ? 'Using collaborative editor' : 'Using simple editor'}
            >
              <FiUsers className="w-4 h-4" />
            </button>

            {/* AI Assistance Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowAiMenu(!showAiMenu)}
                disabled={aiAssisting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  aiAssisting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <FiZap className={`w-4 h-4 ${aiAssisting ? 'animate-pulse' : ''}`} />
                {aiAssisting ? 'AI Working...' : 'AI Assist'}
              </button>

              {showAiMenu && !aiAssisting && (
                <div className="absolute left-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Quick Actions</div>
                    <button
                      onClick={() => handleAiAssist('suggest-improvements')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiZap className="w-4 h-4 text-purple-600" />
                      <div>
                        <div className="font-medium">Suggest Improvements</div>
                        <div className="text-xs text-gray-500">Review clarity & completeness</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('generate-acceptance-criteria')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiCheckCircle className="w-4 h-4 text-green-600" />
                      <div>
                        <div className="font-medium">Generate Acceptance Criteria</div>
                        <div className="text-xs text-gray-500">Create Given-When-Then tests</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('generate-technical-design')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiCpu className="w-4 h-4 text-blue-600" />
                      <div>
                        <div className="font-medium">Technical Design</div>
                        <div className="text-xs text-gray-500">APIs, data models, NFRs</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('generate-uat')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiClipboard className="w-4 h-4 text-yellow-600" />
                      <div>
                        <div className="font-medium">UAT Scenarios</div>
                        <div className="text-xs text-gray-500">Generate test cases</div>
                      </div>
                    </button>

                    <div className="px-3 py-2 mt-2 text-xs font-semibold text-gray-500 uppercase border-t">PM Frameworks</div>
                    <button
                      onClick={() => handleAiAssist('apply-framework', 'DMAIC')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiTarget className="w-4 h-4 text-orange-600" />
                      <div>
                        <div className="font-medium">Apply DMAIC</div>
                        <div className="text-xs text-gray-500">Define, Measure, Analyze, Improve, Control</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('apply-framework', 'SIPOC')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiTarget className="w-4 h-4 text-indigo-600" />
                      <div>
                        <div className="font-medium">Apply SIPOC</div>
                        <div className="text-xs text-gray-500">Suppliers, Inputs, Process, Outputs, Customers</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('apply-framework', 'FMEA')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiTarget className="w-4 h-4 text-red-600" />
                      <div>
                        <div className="font-medium">Apply FMEA</div>
                        <div className="text-xs text-gray-500">Failure Modes & Effects Analysis</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleAiAssist('apply-framework', 'VOC')}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <FiTarget className="w-4 h-4 text-teal-600" />
                      <div>
                        <div className="font-medium">Apply VOC</div>
                        <div className="text-xs text-gray-500">Voice of Customer Analysis</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {card.tags?.s3_url && (
            <button
              onClick={openInNewTab}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiExternalLink className="w-4 h-4" />
              Open Original
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Editor/Preview Area */}
          <div className={`flex-1 overflow-auto p-6 ${showReviewPanel ? 'border-r border-gray-200' : ''}`}>
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : useCollaborativeEditor && isEditing ? (
              <CardEditor
                cardId={card.card_id}
                userId={userEmail}
                userName={userName}
                cardType={card.card_type}
                readOnly={false}
                initialContent={markdown}
                onChange={(content) => setMarkdown(content)}
                onSave={(content) => {
                  setMarkdown(content);
                  handleSave();
                }}
              />
            ) : isEditing ? (
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                className="w-full h-full min-h-[500px] font-mono text-sm p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Write markdown here..."
              />
            ) : (
              <div
                className="prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
              />
            )}
          </div>

          {/* Review Comments Panel */}
          {showReviewPanel && (
            <div className="w-80 flex flex-col bg-gray-50 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-white">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FiMessageSquare className="w-4 h-4" />
                  Review Comments
                </h3>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {reviewComments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No comments yet. Be the first to add feedback.
                  </p>
                ) : (
                  reviewComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                            {comment.user_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {comment.user_name || comment.user_email}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${getCommentTypeStyle(comment.comment_type)}`}>
                          {comment.comment_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.comment}</p>
                    </div>
                  ))
                )}
              </div>

              {/* New Comment Form */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="mb-2">
                  <select
                    value={commentType}
                    onChange={(e) => setCommentType(e.target.value as ReviewComment['comment_type'])}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="general">General Comment</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="approval">Approval</option>
                    <option value="rejection">Request Changes</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add your feedback..."
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={2}
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={isSubmittingComment || !newComment.trim()}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmittingComment ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <FiSend className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            Created by {card.created_by} • {new Date(card.created_at).toLocaleDateString()}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardDetailModal;
